/**
 * Разбор ответа брокера по нетарифке (Л2) и вычисляемые признаки позиции.
 * Одни и те же правила на сайте (предпросмотр загрузки) и на сервере (запись),
 * поэтому они здесь, в shared. Образец ответа — docs/logist-role-brief.md, §5.2.
 */
import { RAISED_CUSTOMS_FEE_RUB, type ChestnyZnak } from '../constants/customs'

/** Ставка пошлины не бывает больше 100 %: всё, что больше единицы, — проценты. */
const MAX_DUTY_RATE = 1

function toNumber(raw: string): number | null {
  const cleaned = raw
    .replace(/[\s\xA0]/g, '')
    .replace(/%$/, '')
    .replace(',', '.')
  if (cleaned === '' || !/^-?\d+(\.\d+)?$/.test(cleaned)) return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

/**
 * Ставка пошлины → доля (0.08 = 8 %). Брокер пишет дробью с запятой («0,08»),
 * вручную вводят процентом («8», «8 %», «7,5»). Правило: знак «%» или число
 * больше единицы — проценты, иначе доля. Пусто или мусор → null.
 */
export function normalizeDutyRate(raw: string | number | null | undefined): number | null {
  if (raw == null) return null
  const isPercent = typeof raw === 'string' && /%\s*$/.test(raw.trim())
  const n = typeof raw === 'number' ? (Number.isFinite(raw) ? raw : null) : toNumber(raw.trim())
  if (n == null || n < 0) return null
  const rate = isPercent || n > MAX_DUTY_RATE ? n / 100 : n
  if (rate > MAX_DUTY_RATE) return null
  return Math.round(rate * 10000) / 10000
}

// Формат выгрузки брокера (его программа, с 29.10.2026): «Да[73860.00]», «Да», «Нет».
const FEE_YES_RE = /^да\s*(?:\[([^\]]*)\])?$/i
const FEE_NO_RE = /^нет$/i

/**
 * Сумма таможенного сбора в рублях: «73860», «73 860», «73860,00», а из файла
 * брокера — «Да[73860.00]» (сумма в скобках), «Да» без суммы (сбор один —
 * 73 860 ₽) и «Нет» (0). Пусто или мусор → null.
 */
export function parseCustomsFee(raw: string | number | null | undefined): number | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (FEE_NO_RE.test(t)) return 0
    const yes = FEE_YES_RE.exec(t)
    if (yes) {
      const inner = yes[1]?.trim()
      if (!inner) return RAISED_CUSTOMS_FEE_RUB
      const amount = parseCustomsFee(inner)
      return amount != null && amount > 0 ? amount : RAISED_CUSTOMS_FEE_RUB
    }
  }
  const n = typeof raw === 'number' ? raw : toNumber(String(raw).trim())
  if (n == null || !Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100) / 100
}

/** Повышенный сбор — любой сбор больше нуля (сейчас он один — 73 860 ₽). */
export function isRaisedFee(fee: number | null | undefined): boolean {
  return fee != null && fee > 0
}

export { RAISED_CUSTOMS_FEE_RUB }

const MONTHS_RU: Record<string, number> = {
  январ: 1,
  феврал: 2,
  март: 3,
  апрел: 4,
  ма: 5,
  июн: 6,
  июл: 7,
  август: 8,
  сентябр: 9,
  октябр: 10,
  ноябр: 11,
  декабр: 12,
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Дата из текста брокера → «YYYY-MM-DD»: «с 1 мая 2026г.», «с 01.09.2026».
 * Нет даты → null.
 */
export function parseRuDateInText(text: string | null | undefined): string | null {
  if (!text) return null
  const numeric = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(text)
  if (numeric) {
    const [, d, m, y] = numeric
    const day = Number(d)
    const month = Number(m)
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12)
      return `${y}-${pad2(month)}-${pad2(day)}`
  }
  const verbal = /(\d{1,2})\s+([а-яё]+)\s+(\d{4})/i.exec(text)
  if (verbal) {
    const [, d, word, y] = verbal
    const lower = word!.toLowerCase()
    // «мая» / «май» — короткий корень «ма» проверяем последним, чтобы не спутать с «марта».
    const key = Object.keys(MONTHS_RU)
      .sort((a, b) => b.length - a.length)
      .find((k) => lower.startsWith(k))
    const day = Number(d)
    if (key && day >= 1 && day <= 31) return `${y}-${pad2(MONTHS_RU[key]!)}-${pad2(day)}`
  }
  return null
}

// \w в JS не ловит кириллицу — окончание «-ый/-ого» задаём классом букв явно.
const ZNAK_RE = /честн[а-яё]*\s+знак/i
// Отрицание: «не требуется / не нужен / не подлежит», «без маркировки», «Честный знак: нет».
// Границу слова после кириллицы задаём явно — \b в JS её не видит.
const ZNAK_NEGATIVE_RE =
  /(не\s+(требуется|нужн|потребуется|подлежит))|(без\s+маркировк)|(знак[»"']?\s*[:—–-]?\s*нет(?![а-яё\w]))/i

/**
 * «Честный знак» по тексту ответа брокера (колонка «Особенности», иногда «Нетарифка»).
 * Упомянут без отрицания → нужен (с датой, если она есть); упомянут с отрицанием →
 * не нужен. Не упомянут вовсе → не нужен: брокер пишет про знак, только когда он требуется.
 */
export function detectChestnyZnak(...texts: Array<string | null | undefined>): {
  value: ChestnyZnak
  from: string | null
} {
  for (const t of texts) {
    if (!t || !ZNAK_RE.test(t)) continue
    if (ZNAK_NEGATIVE_RE.test(t)) return { value: 'NO', from: null }
    return { value: 'YES', from: parseRuDateInText(t) }
  }
  return { value: 'NO', from: null }
}

const LICENSE_RE = /лиценз/i
const NOTIFICATION_RE = /нотификац/i

/** Нужны ли разрешительные документы: текст «Нетарифки» есть и не начинается с «нет». */
export function documentsRequired(nonTariffNote: string | null | undefined): boolean {
  const t = nonTariffNote?.trim()
  if (!t) return false
  // «нет», «Нет (маркировки EAC не должно быть)»; \b после кириллицы не работает — граница явно.
  return !/^нет(?![а-яё\w])/i.test(t) && !/^не\s+требует/i.test(t)
}

export interface CustomsAttentionInput {
  nonTariffNote?: string | null
  certificateNote?: string | null
  featuresNote?: string | null
  customsFeeRaised?: boolean | null
  chestnyZnak?: ChestnyZnak | null
}

/**
 * Причины красного флага «внимание» для менеджера (бриф §9.2): лицензия,
 * нотификация (такие позиции заказчик старается не возить), повышенный сбор,
 * «Честный знак». Пустой список — флага нет.
 */
export function customsAttentionReasons(input: CustomsAttentionInput): string[] {
  const text = [input.nonTariffNote, input.certificateNote, input.featuresNote]
    .filter(Boolean)
    .join(' \n ')
  const reasons: string[] = []
  if (LICENSE_RE.test(text)) reasons.push('Лицензия')
  if (NOTIFICATION_RE.test(text)) reasons.push('Нотификация')
  if (input.customsFeeRaised) reasons.push('Повышенный сбор')
  if (input.chestnyZnak === 'YES') reasons.push('Честный знак')
  return reasons
}

/** Код ТН ВЭД: только цифры, 10 знаков (пробелы брокера убираем). Иначе null. */
export function normalizeTnvedCode(raw: string | number | null | undefined): string | null {
  if (raw == null) return null
  const digits = String(raw).replace(/[\s\xA0.]/g, '')
  return /^\d{10}$/.test(digits) ? digits : null
}

/** Ставка пошлины для показа: 0.08 → «8 %», 0.075 → «7,5 %». */
export function formatDutyRate(rate: number | string | null | undefined): string {
  if (rate == null || rate === '') return '—'
  const n = typeof rate === 'string' ? Number(rate) : rate
  if (!Number.isFinite(n)) return '—'
  const pct = Math.round(n * 10000) / 100
  return `${pct.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} %`
}

/** «Честный знак» своей проверки позиции: значение, дата «с» и когда проверили. */
export interface ZnakOfCheck {
  chestnyZnak: ChestnyZnak
  chestnyZnakFrom: string | null
  checkedAt: string | null
}

/** «Честный знак» из памяти по партномеру и дата последней проверки. */
export interface ZnakOfHint {
  chestnyZnak: ChestnyZnak
  chestnyZnakFrom: string | null
  lastCheckedAt: string
}

export interface ResolvedZnak {
  value: ChestnyZnak
  from: string | null
  /** Значение из памяти по партномеру (прошлые проверки), а не из ответа по позиции. */
  fromArchive: boolean
}

/**
 * Какой «Честный знак» показывать у позиции. Своё известное значение — всегда оно.
 * Своё «Неизвестно» в записанном ответе (логист так и сохранил) — тоже своё: память
 * его не перебивает, иначе после правки «Не нужен» → «Неизвестно» позиция снова
 * показала бы «Не нужен (архив)». Память подставляется, только если своего ответа
 * ещё нет или память свежее ответа (партномер проверили позже по другой позиции).
 * Ничего не известно — null.
 */
export function resolveCustomsZnak(
  check: ZnakOfCheck | null | undefined,
  hint: ZnakOfHint | null | undefined,
): ResolvedZnak | null {
  if (check && check.chestnyZnak !== 'UNKNOWN') {
    return { value: check.chestnyZnak, from: check.chestnyZnakFrom, fromArchive: false }
  }
  const hintKnown = hint && hint.chestnyZnak !== 'UNKNOWN' ? hint : null
  const answered = check?.checkedAt ?? null
  if (hintKnown && (!answered || hintKnown.lastCheckedAt > answered)) {
    return { value: hintKnown.chestnyZnak, from: hintKnown.chestnyZnakFrom, fromArchive: true }
  }
  if (answered) return { value: 'UNKNOWN', from: null, fromArchive: false }
  return null
}

/** Ссылка на даташит: только http(s) — иначе в поле можно вписать javascript:-ссылку. */
export const DATASHEET_URL_RE = /^https?:\/\/[^\s]+$/i
export const DATASHEET_URL_MAX = 1000

export function isSafeDatasheetUrl(url: string | null | undefined): url is string {
  return !!url && url.length <= DATASHEET_URL_MAX && DATASHEET_URL_RE.test(url)
}

/**
 * Срок, после которого ответ по нетарифке — только справка (решение Сергея 19.10.2026,
 * разговор с логистом): данные по партномеру меняются (при перепроверке у каждого
 * пятого парта что-то менялось), поэтому ответ старше месяца перед закупкой
 * перепроверяют — отправляют позицию логисту заново.
 */
export const CUSTOMS_ANSWER_STALE_DAYS = 30

const DAY_MS = 24 * 60 * 60 * 1000

/** Ответ (дата проверки) старше месяца. Нет даты или она нечитаема — не «устарел». */
export function isCustomsAnswerStale(
  checkedAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!checkedAt) return false
  const at = checkedAt instanceof Date ? checkedAt : new Date(checkedAt)
  if (Number.isNaN(at.getTime())) return false
  return now.getTime() - at.getTime() > CUSTOMS_ANSWER_STALE_DAYS * DAY_MS
}
